import { Dispatch } from 'redux';
import { campaignApi } from 'components/campaigns/campaignApi';
import { CampaignDefineFormData } from 'components/campaigns/forms/CreateCampaignDefine';
import { Campaign, CampaignState } from 'components/campaigns/models/Campaign';
import {
    CampaignItem,
    CampaignItemConditionRequestData,
    generateConditionData,
    generateEffectData,
    generateEffectRequestData,
    isCampaignItem
} from 'components/campaigns/models/CampaignItem';
import { CampaignTrigger } from 'components/campaigns/models/CampaignTrigger';
import {
    getConditionFieldComparator,
    getConditionFieldValue
} from 'components/campaigns/models/CampaignTriggerCondition';
import { Perk } from 'components/loyalty/models/PerkModel';
import {
    MESSAGE_MARKETING_CAMPAIGN_ITEM_CREATE_ERROR,
    MESSAGE_MARKETING_CAMPAIGN_ITEM_CREATE_SUCCESS,
    MESSAGE_MARKETING_CAMPAIGN_ITEM_DELETE_ERROR,
    MESSAGE_MARKETING_CAMPAIGN_ITEM_DELETE_SUCCESS,
    MESSAGE_MARKETING_CAMPAIGN_ITEM_UPDATE_ERROR,
    MESSAGE_MARKETING_CAMPAIGN_ITEM_UPDATE_SUCCESS,
    MESSAGE_MARKETING_CAMPAIGN_LOADING_ERROR
} from 'config/messages';
import logger from 'lib/logger';
import { enqueueSnackbar } from 'store/notifications/notificationsActions';
import { ApplicationState } from 'store/store';
import { CampaignsActionType } from './campaignsActionTypes';

interface IBaseAction {
    type: CampaignsActionType;
}

interface ProgressAction extends IBaseAction {
    type: CampaignsActionType.END_REQUEST | CampaignsActionType.START_REQUEST;
}

interface ToggleModalAction extends IBaseAction {
    type: CampaignsActionType.TOGGLE_CREATE_AUDIENCE_MODAL | CampaignsActionType.TOGGLE_CREATE_PERK_MODAL;
    title?: string;
    index: number;
}

interface ToggleVoucherModalAction extends IBaseAction {
    type: CampaignsActionType.TOGGLE_CREATE_VOUCHER_MODAL;
    title?: string;
    index: [number, number];
}

interface CreateCampaignAction extends IBaseAction {
    type: CampaignsActionType.ADD_CAMPAIGN_ITEM;
    campaignItem: CampaignItem;
}

interface UpdateCampaignAction extends IBaseAction {
    type: CampaignsActionType.UPDATE_CAMPAIGN_ITEM;
    campaignItem: CampaignItem;
    index: number;
}

interface CrudCampaignAction extends IBaseAction {
    type:
        | CampaignsActionType.CRUD_CAMPAIGNS_ABORT
        | CampaignsActionType.CRUD_CAMPAIGNS_SUCCESS
        | CampaignsActionType.ADD_EMPTY_CAMPAIGN;
}

interface CurrentCampaignAction extends IBaseAction {
    type: CampaignsActionType.CREATE_CAMPAIGN_SUCCESS;
    campaign: Campaign;
}

interface UpdateCampaignEffectTargetAction extends IBaseAction {
    type: CampaignsActionType.UPDATE_CAMPAIGN_EFFECT_TARGET;
    target: string;
}

interface UpdateCampaignConditionVariantAction extends IBaseAction {
    type: CampaignsActionType.UPDATE_CAMPAIGN_CONDITION_VARIANT_VALUE;
    value: string;
}

interface DiscardModalsAction extends IBaseAction {
    type:
        | CampaignsActionType.CLOSE_CAMPAIGN_MODALS
        | CampaignsActionType.CLOSE_CAMPAIGN_DISCARD_MODALS
        | CampaignsActionType.DISCARD_CAMPAIGN_AUDIENCE_CREATION
        | CampaignsActionType.DISCARD_CAMPAIGN_PERK_CREATION
        | CampaignsActionType.DISCARD_CAMPAIGN_VOUCHER_CREATION;
}

interface DeleteCampaignItemAction extends IBaseAction {
    type: CampaignsActionType.DELETE_CAMPAIGN_ITEM;
    index: number;
}

interface LoadCampaignAction extends IBaseAction {
    type: CampaignsActionType.LOAD_CAMPAIGN_SUCCESS;
    campaign: CampaignDefineFormData & { id: string };
    campaignItems: CampaignItem[];
}

export type ICampaignsAction =
    | ProgressAction
    | ToggleModalAction
    | ToggleVoucherModalAction
    | UpdateCampaignAction
    | UpdateCampaignEffectTargetAction
    | CreateCampaignAction
    | CurrentCampaignAction
    | CrudCampaignAction
    | DiscardModalsAction
    | DeleteCampaignItemAction
    | UpdateCampaignConditionVariantAction
    | LoadCampaignAction;

export const toggleCreatePerkModal =
    (index: number, title?: string) => (dispatch: Dispatch<ToggleModalAction>) => {
        dispatch<ToggleModalAction>({
            index,
            title,
            type: CampaignsActionType.TOGGLE_CREATE_PERK_MODAL
        });
    };

export const toggleCreateAudienceModal =
    (index: number, title?: string) => (dispatch: Dispatch<ToggleModalAction>) => {
        dispatch({
            index,
            title,
            type: CampaignsActionType.TOGGLE_CREATE_AUDIENCE_MODAL
        });
    };

export const toggleCreateVoucherModal =
    (index: [number, number], title?: string) => (dispatch: Dispatch<ToggleVoucherModalAction>) => {
        dispatch({
            index,
            title,
            type: CampaignsActionType.TOGGLE_CREATE_VOUCHER_MODAL
        });
    };

export const closeCampaignModals = () => (dispatch: Dispatch<DiscardModalsAction>) => {
    dispatch({
        type: CampaignsActionType.CLOSE_CAMPAIGN_MODALS
    });
};

export const closeCampaignDiscardModals = () => (dispatch: Dispatch<DiscardModalsAction>) => {
    dispatch({
        type: CampaignsActionType.CLOSE_CAMPAIGN_DISCARD_MODALS
    });
};

export const discardCampaignAudienceModal = () => (dispatch: Dispatch<DiscardModalsAction>) => {
    dispatch({
        type: CampaignsActionType.DISCARD_CAMPAIGN_AUDIENCE_CREATION
    });
};

export const discardCampaignPerkModal = () => (dispatch: Dispatch<DiscardModalsAction>) => {
    dispatch({
        type: CampaignsActionType.DISCARD_CAMPAIGN_PERK_CREATION
    });
};

export const discardCampaignVoucherkModal = () => (dispatch: Dispatch<DiscardModalsAction>) => {
    dispatch({
        type: CampaignsActionType.DISCARD_CAMPAIGN_VOUCHER_CREATION
    });
};

export const updateCampaignEffectTarget =
    (target: string) => (dispatch: Dispatch<UpdateCampaignEffectTargetAction>) => {
        dispatch<UpdateCampaignEffectTargetAction>({
            target,
            type: CampaignsActionType.UPDATE_CAMPAIGN_EFFECT_TARGET
        });
    };

export const updateCampaignConditionVariant =
    (value: string) => (dispatch: Dispatch<UpdateCampaignConditionVariantAction>) => {
        dispatch<UpdateCampaignConditionVariantAction>({
            value,
            type: CampaignsActionType.UPDATE_CAMPAIGN_CONDITION_VARIANT_VALUE
        });
    };

export const deleteCampaignItem =
    (index: number) => async (dispatch: Dispatch, getState: () => ApplicationState) => {
        dispatch({
            type: CampaignsActionType.START_REQUEST
        });
        const {
            campaigns: { currentCampaign, campaignItems }
        } = getState();
        if (!campaignItems[index].id) {
            dispatch<DeleteCampaignItemAction>({
                index,
                type: CampaignsActionType.DELETE_CAMPAIGN_ITEM
            });
            dispatch({
                type: CampaignsActionType.END_REQUEST
            });
        } else {
            const result = await campaignApi.deleteTrigger(currentCampaign.id, campaignItems[index].id);
            if (!result.ok) {
                dispatch({
                    type: CampaignsActionType.END_REQUEST
                });
                return dispatch(
                    enqueueSnackbar(MESSAGE_MARKETING_CAMPAIGN_ITEM_DELETE_ERROR, { variant: 'error' })
                );
            }
            dispatch<DeleteCampaignItemAction>({
                index,
                type: CampaignsActionType.DELETE_CAMPAIGN_ITEM
            });
            dispatch({
                type: CampaignsActionType.END_REQUEST
            });
            dispatch(enqueueSnackbar(MESSAGE_MARKETING_CAMPAIGN_ITEM_DELETE_SUCCESS, { variant: 'info' }));
        }
    };

export const submitCampaignItemAction =
    (item: CampaignItem, index: number, campaignId: string, perks: Perk[]) => async (dispatch: Dispatch) => {
        if (isCampaignItem(item, perks)) {
            dispatch({
                type: CampaignsActionType.START_REQUEST
            });
            const conditionData = item.condition.value.reduce<CampaignItemConditionRequestData>(
                (acc, conditionField) => {
                    if (!!conditionField.key && !!conditionField.value) {
                        acc[conditionField.key] = {
                            value: getConditionFieldValue(conditionField),
                            comparator: getConditionFieldComparator(conditionField)
                        };
                    }
                    return acc;
                },
                {}
            );
            const effectData = generateEffectRequestData(item, perks);
            const condition = { type: item.condition.type, data: conditionData };
            const effect = { type: item.effect.type, data: effectData };
            if (item.id) {
                const result = await campaignApi.updateTrigger(campaignId, item.id, { condition, effect });
                if (!result.ok) {
                    dispatch({
                        type: CampaignsActionType.CRUD_CAMPAIGNS_ABORT
                    });
                    dispatch({
                        type: CampaignsActionType.END_REQUEST
                    });
                    return dispatch(
                        enqueueSnackbar(MESSAGE_MARKETING_CAMPAIGN_ITEM_UPDATE_ERROR, { variant: 'error' })
                    );
                }
                dispatch(
                    enqueueSnackbar(MESSAGE_MARKETING_CAMPAIGN_ITEM_UPDATE_SUCCESS, { variant: 'info' })
                );
            } else {
                const result = await campaignApi.createTrigger(campaignId, { condition, effect });
                if (!result.ok) {
                    dispatch({
                        type: CampaignsActionType.CRUD_CAMPAIGNS_ABORT
                    });
                    dispatch({
                        type: CampaignsActionType.END_REQUEST
                    });
                    return dispatch(
                        enqueueSnackbar(MESSAGE_MARKETING_CAMPAIGN_ITEM_CREATE_ERROR, { variant: 'error' })
                    );
                }
                const campaignItem: CampaignItem = {
                    origin: result.body,
                    effect: generateEffectData(result.body),
                    condition: generateConditionData(result.body),
                    id: result.body.id
                };
                dispatch({
                    campaignItem,
                    index,
                    type: CampaignsActionType.UPDATE_CAMPAIGN_ITEM
                });
                dispatch(
                    enqueueSnackbar(MESSAGE_MARKETING_CAMPAIGN_ITEM_CREATE_SUCCESS, { variant: 'info' })
                );
            }
            dispatch({
                type: CampaignsActionType.END_REQUEST
            });
        }
    };

export const submitCampaignItem =
    (index: number) => (dispatch: Dispatch, getState: () => ApplicationState) => {
        const {
            campaigns: { campaignItems, currentCampaign },
            perks: { perks }
        } = getState();
        const item = campaignItems[index];
        submitCampaignItemAction(item, index, currentCampaign.id, perks)(dispatch);
    };

export const loadCampaign = (campaignId: string, onError: () => void) => async (dispatch: Dispatch) => {
    try {
        dispatch({
            type: CampaignsActionType.START_REQUEST
        });
        const campaignResult = await campaignApi.get(campaignId);
        if (!campaignResult.ok) {
            throw new Error(campaignResult.body.message);
        }
        const loadedCampaign: Campaign = campaignResult.body;
        if (
            loadedCampaign.state !== CampaignState.DRAFTED &&
            loadedCampaign.state !== CampaignState.START_FAILED
        ) {
            dispatch(enqueueSnackbar(MESSAGE_MARKETING_CAMPAIGN_LOADING_ERROR, { variant: 'error' }));
        }
        const currentCampaign = {
            id: loadedCampaign.id,
            title: loadedCampaign.title,
            description: loadedCampaign.description || ''
        };
        const triggersResult = await campaignApi.getTriggersList(campaignId);
        if (!triggersResult.ok) {
            throw new Error(triggersResult.body.message);
        }
        const loadedTriggers: CampaignTrigger[] = triggersResult.body.items;
        const campaignItems = loadedTriggers.map(item => ({
            id: item.id,
            origin: item,
            effect: generateEffectData(item),
            condition: generateConditionData(item)
        }));
        dispatch<LoadCampaignAction>({
            campaignItems,
            type: CampaignsActionType.LOAD_CAMPAIGN_SUCCESS,
            campaign: currentCampaign
        });
    } catch (error) {
        logger.error(`Failed to load a campaign with id ${campaignId}`, error.message);
        onError();
    } finally {
        dispatch({
            type: CampaignsActionType.END_REQUEST
        });
    }
};
